import L from "leaflet";
import "polyline-encoded";
import apiController from "@/utils/apiController";

/**
 * Serializes LatLng list to the DB `shape` JSON format.
 * @param {Array<{lat:number,lng:number}>} latlngs
 * @returns {string}
 */
export function buildShape(latlngs) {
  const shape = (latlngs || []).map((l) => ({
    lt: l.lat,
    lg: l.lng,
  }));
  return JSON.stringify(shape);
}

/**
 * Index of the polyline vertex nearest to (lat, lng).
 * @param {Array<{lat:number,lng:number}>} latlngs
 * @param {number} stopLat
 * @param {number} stopLng
 * @returns {number}
 */
export function nearestSplitIndex(latlngs, stopLat, stopLng) {
  if (!latlngs || !latlngs.length) return 0;
  const tLat = Number(stopLat);
  const tLng = Number(stopLng);
  let bestI = 0;
  let best = Infinity;
  for (let i = 0; i < latlngs.length; i++) {
    const p = latlngs[i];
    const d = (p.lat - tLat) * (p.lat - tLat) + (p.lng - tLng) * (p.lng - tLng);
    if (d < best) {
      best = d;
      bestI = i;
    }
  }
  return bestI;
}

/**
 * Map temporary vias onto stop anchors along the current polyline.
 * @returns {Array<{lat:number,lng:number,polyIdx:number,stopAnchor:number,originalIdx:number}>}
 */
export function sortViasForPlan({
  latlngs,
  stopsLngLat,
  hasStart,
  hasEnd,
  startCoord,
  endCoord,
  editWaypoints,
}) {
  const stops = stopsLngLat || [];
  const anchorPoints = [];
  if (hasStart && startCoord) {
    anchorPoints.push({ lat: startCoord[1], lng: startCoord[0] });
  }
  stops.forEach((s) => anchorPoints.push({ lat: s[1], lng: s[0] }));
  if (hasEnd && endCoord) {
    anchorPoints.push({ lat: endCoord[1], lng: endCoord[0] });
  }

  const anchorIndices = anchorPoints.map((p) =>
    nearestSplitIndex(latlngs, p.lat, p.lng)
  );

  const vias = (editWaypoints || []).map((v, originalIdx) => {
    const polyIdx = nearestSplitIndex(latlngs, v.lat, v.lng);
    let stopAnchor = stops.length;
    for (let i = anchorIndices.length - 1; i >= 0; i--) {
      if (anchorIndices[i] <= polyIdx) {
        stopAnchor = Math.max(0, i + 1 - (hasStart ? 1 : 0));
        break;
      }
      if (i === 0) stopAnchor = 0;
    }
    stopAnchor = Math.max(0, Math.min(stops.length, stopAnchor));
    return { lat: v.lat, lng: v.lng, polyIdx, stopAnchor, originalIdx };
  });
  vias.sort((a, b) => a.polyIdx - b.polyIdx || a.originalIdx - b.originalIdx);
  return vias;
}

/**
 * Build Verso /plan payload (jobs + vehicles with forced step order).
 * Coords are [lng, lat].
 */
export function buildVersoPlanPayload({
  stopCoordsLngLat,
  viasOrdenadas,
  startCoord,
  endCoord,
  serviceTime = 0,
}) {
  const baseStops = (stopCoordsLngLat || []).map((c, i) => ({
    id: i + 1,
    location: [Number(c[0]), Number(c[1])],
    service: serviceTime,
  }));
  const vias = Array.isArray(viasOrdenadas) ? viasOrdenadas : [];
  const viaJobs = vias.map((v, i) => ({
    id: baseStops.length + i + 1,
    location: [Number(v.lng), Number(v.lat)],
    service: serviceTime,
    _stopAnchor: v.stopAnchor,
  }));

  const orderedJobIds = [];
  let viaCursor = 0;
  const sortedVias = [...viaJobs].sort((a, b) => a._stopAnchor - b._stopAnchor);
  for (let i = 0; i <= baseStops.length; i++) {
    while (viaCursor < sortedVias.length && sortedVias[viaCursor]._stopAnchor <= i) {
      orderedJobIds.push(sortedVias[viaCursor].id);
      viaCursor++;
    }
    if (i < baseStops.length) orderedJobIds.push(baseStops[i].id);
  }
  while (viaCursor < sortedVias.length) {
    orderedJobIds.push(sortedVias[viaCursor].id);
    viaCursor++;
  }

  const steps = [];
  if (startCoord) steps.push({ type: "start" });
  orderedJobIds.forEach((id) => steps.push({ type: "job", id }));
  if (endCoord) steps.push({ type: "end" });

  const jobs = [
    ...baseStops,
    ...viaJobs.map((j) => ({ id: j.id, location: j.location, service: serviceTime })),
  ];
  const totalDemand = jobs.length + 1;
  const vehicle = {
    id: 1,
    profile: "car",
    capacity: [Math.max(9999, totalDemand * 10)],
    steps,
  };
  if (startCoord) vehicle.start = startCoord;
  if (endCoord) vehicle.end = endCoord;

  return { jobs, vehicles: [vehicle] };
}

/**
 * Decode Verso encoded polyline into latlngs, GeoJSON and shape.
 * @param {string} geometry
 * @returns {{ latlngs: Array, geojson: object, shape: string } | null}
 */
export function decodePlanGeometry(geometry) {
  if (!geometry) return null;
  const poly = L.Polyline.fromEncoded(geometry);
  const latlngs = poly.getLatLngs();
  if (!latlngs || !latlngs.length) return null;
  const leafletPoly = L.polyline(latlngs);
  return {
    latlngs,
    geojson: leafletPoly.toGeoJSON(),
    shape: buildShape(latlngs),
  };
}

/**
 * Extract [lng, lat] coords from GeoJSON LineString / Feature.
 */
export function lngLatFromGeojson(gj) {
  if (!gj) return [];
  let geom = gj;
  if (gj.type === "Feature") geom = gj.geometry;
  if (gj.type === "FeatureCollection" && gj.features && gj.features[0]) {
    geom = gj.features[0].geometry;
  }
  if (!geom || !geom.coordinates) return [];
  if (geom.type === "LineString") {
    return geom.coordinates.map((c) => [c[0], c[1]]);
  }
  if (geom.type === "MultiLineString") {
    return (geom.coordinates[0] || []).map((c) => [c[0], c[1]]);
  }
  return [];
}

function vroomRequestError(err) {
  const status = err && err.response && err.response.status;
  const errData = err && err.response && err.response.data;
  const codigo = errData && errData.codigo;
  if (status === 429 || codigo === "RATE_LIMIT_VROOM") {
    return { ok: false, code: "RATE_LIMIT" };
  }
  const detail =
    (errData && (errData.message || errData.error || errData.mensagem)) ||
    (err && err.message) ||
    null;
  return { ok: false, code: "BACKEND_ERROR", status: status || null, detail };
}

function vroomPayloadError(data, fallbackCode) {
  return {
    ok: false,
    code: fallbackCode || "NO_SOLUTION",
    detail: (data && (data.error || data.message || data.mensagem)) || null,
  };
}

/**
 * POST /verso/solve (VROOM escolhe a ordem dos jobs) e depois POST /verso/plan
 * com `vehicle.steps` forçados nessa ordem, para obter a geometria detalhada.
 *
 * `jobs[].id` deve ser 1..N na ordem de entrada. `vehicle.steps` é ignorado no solve.
 *
 * @returns {Promise<{
 *   ok: boolean,
 *   code?: string,
 *   detail?: string|null,
 *   newStopOrder?: number[],
 *   geometry?: string,
 *   distance?: number,
 *   duration?: number,
 *   etas?: number[],
 * }>}
 */
export async function versoSolveThenPlan({ jobs, vehicle } = {}) {
  if (!Array.isArray(jobs) || jobs.length === 0) {
    return { ok: false, code: "NO_STOPS" };
  }
  if (!vehicle) {
    return { ok: false, code: "NO_ROUTE" };
  }

  const vehicleSolve = { ...vehicle };
  delete vehicleSolve.steps;

  try {
    const solveRes = await apiController.request(apiController.endpoints.versoSolve, {
      data: { jobs, vehicles: [vehicleSolve] },
      silent: true,
      config: { timeout: 120000 },
    });
    const solveData = solveRes && solveRes.data;
    if (
      !solveData ||
      solveData.code !== 0 ||
      !Array.isArray(solveData.routes) ||
      !solveData.routes.length
    ) {
      return vroomPayloadError(solveData, "NO_SOLUTION");
    }
    if (Array.isArray(solveData.unassigned) && solveData.unassigned.length > 0) {
      return {
        ok: false,
        code: "UNASSIGNED",
        unassignedCount: solveData.unassigned.length,
      };
    }

    const solved = solveData.routes[0];
    const solveJobSteps = (solved.steps || []).filter(
      (s) => String(s.type || "").toLowerCase() === "job"
    );
    if (solveJobSteps.length !== jobs.length) {
      return { ok: false, code: "INCOMPLETE_SOLUTION" };
    }

    const planSteps = [];
    if (Array.isArray(vehicleSolve.start)) planSteps.push({ type: "start" });
    solveJobSteps.forEach((st) => {
      const jobId = st.id != null ? Number(st.id) : Number(st.job);
      if (Number.isFinite(jobId)) planSteps.push({ type: "job", id: jobId });
    });
    if (Array.isArray(vehicleSolve.end)) planSteps.push({ type: "end" });

    const vehiclePlan = { ...vehicleSolve, steps: planSteps };
    const planRes = await apiController.request(apiController.endpoints.versoPlan, {
      data: { jobs, vehicles: [vehiclePlan] },
      silent: true,
    });
    const planData = planRes && planRes.data;
    if (
      !planData ||
      planData.code !== 0 ||
      !Array.isArray(planData.routes) ||
      !planData.routes.length
    ) {
      return vroomPayloadError(planData, "NO_SOLUTION");
    }

    const planned = planData.routes[0];
    if (!planned || !planned.geometry) {
      return { ok: false, code: "NO_GEOMETRY" };
    }

    const planJobSteps = (planned.steps || []).filter(
      (s) => String(s.type || "").toLowerCase() === "job"
    );
    const newStopOrder = solveJobSteps.map((step) => Number(step.id) - 1);
    const etas = planJobSteps.map((step) => Number(step.arrival));

    return {
      ok: true,
      newStopOrder,
      geometry: planned.geometry,
      distance: Number(planned.distance) || 0,
      duration: Number(planned.duration) || 0,
      etas,
    };
  } catch (err) {
    return vroomRequestError(err);
  }
}

const PIN_COORD_EPS = 1e-5;

export function sameLngLat(coord, lng, lat, eps = PIN_COORD_EPS) {
  return (
    Array.isArray(coord) &&
    coord.length >= 2 &&
    Number.isFinite(Number(lng)) &&
    Number.isFinite(Number(lat)) &&
    Math.abs(Number(coord[0]) - Number(lng)) < eps &&
    Math.abs(Number(coord[1]) - Number(lat)) < eps
  );
}

/**
 * Jobs/vehicle para re-solve de rota principal da tela de Roteirização.
 * Destino (analogia tipo 3) sempre pinado. Origem (tipo 2) só com deslocamento.
 */
export function buildMainRouteJobsAndVehicle({
  orderedStops,
  sentido,
  hasDisplacement = false,
  planStartCoord = null,
  planEndCoord = null,
  vehicleId = 1,
  capacity = 9999,
  mapJob = null,
} = {}) {
  const volta = Number(sentido) === 2;
  const destCoord = volta
    ? (Array.isArray(planStartCoord) ? planStartCoord : null)
    : (Array.isArray(planEndCoord) ? planEndCoord : null);
  const pinPassengerStart = !volta && !!hasDisplacement;
  const pinPassengerEnd = volta && !!hasDisplacement;

  const vehicle = {
    id: vehicleId,
    profile: "car",
    capacity: Array.isArray(capacity) ? capacity : [Number(capacity) || 9999],
  };
  if (volta) {
    if (Array.isArray(planStartCoord)) vehicle.start = planStartCoord.slice();
    if (pinPassengerEnd && Array.isArray(planEndCoord)) vehicle.end = planEndCoord.slice();
  } else {
    if (Array.isArray(planEndCoord)) vehicle.end = planEndCoord.slice();
    if (pinPassengerStart && Array.isArray(planStartCoord)) vehicle.start = planStartCoord.slice();
  }

  const pinStartCoord = pinPassengerStart && Array.isArray(vehicle.start) ? vehicle.start : null;
  const pinEndCoord = pinPassengerEnd && Array.isArray(vehicle.end) ? vehicle.end : null;

  const jobsStops = [];
  const skipped = [];
  (orderedStops || []).forEach((stop, origIdx) => {
    if (!stop) return;
    const lng = Number(stop.lng);
    const lat = Number(stop.lat);
    const isDest = !!(destCoord && sameLngLat(destCoord, lng, lat));
    const isPinStart = !!(pinStartCoord && sameLngLat(pinStartCoord, lng, lat));
    const isPinEnd = !!(pinEndCoord && sameLngLat(pinEndCoord, lng, lat));
    if (isDest || isPinStart || isPinEnd) {
      skipped.push({ stop, origIdx, isDest, isPinStart, isPinEnd });
      return;
    }
    jobsStops.push({ stop, origIdx });
  });

  const jobById = new Map();
  const jobs = jobsStops.map((entry, i) => {
    const id = i + 1;
    jobById.set(id, entry);
    if (typeof mapJob === "function") return mapJob(entry.stop, id);
    return {
      id,
      location: [Number(entry.stop.lng), Number(entry.stop.lat)],
      service: 0,
    };
  });

  return {
    jobs,
    vehicle,
    jobById,
    jobsStops,
    skipped,
    pinPassengerStart,
    pinPassengerEnd,
    volta,
    destCoord,
  };
}

/** Reinsere termos pinados nas pontas após o /solve (newStopOrder em índice de job). */
export function remapSolveOrderWithPinnedTermini(built, jobIndexOrder) {
  const reordered = (jobIndexOrder || [])
    .map((ji) => built && built.jobsStops && built.jobsStops[ji])
    .filter(Boolean);
  const skipped = (built && built.skipped) || [];
  const startPinned = skipped.filter((s) => s.isPinStart);
  const endPinned = skipped.filter((s) => s.isPinEnd);
  const destPinned = skipped.filter((s) => s.isDest && !s.isPinStart && !s.isPinEnd);
  const sequence = built && built.volta
    ? [...destPinned, ...reordered, ...endPinned]
    : [...startPinned, ...reordered, ...destPinned];
  const used = new Set(sequence.map((e) => e.origIdx));
  skipped.forEach((s) => {
    if (!used.has(s.origIdx)) sequence.push(s);
  });
  return sequence.map((e) => e.origIdx);
}

export function alignEtasToPinnedOrder(built, origIdxOrder, jobEtas) {
  const order = origIdxOrder || [];
  const etas = jobEtas || [];
  let cursor = 0;
  const jobOrig = new Set(((built && built.jobsStops) || []).map((e) => e.origIdx));
  return order.map((origIdx) => {
    if (!jobOrig.has(origIdx)) return 0;
    const v = Number(etas[cursor]);
    cursor += 1;
    return Number.isFinite(v) ? v : 0;
  });
}
